// Promos de cada forma de pago: monto, una fila de chips (conserva, pierde, gana) y «Ver detalle».
import { useState } from 'react';
import { useQueryClient, type QueryClient } from '@tanstack/react-query';
import { useCatalog } from '@/domains/sales/wizard/api/useWizard';
import { useCatalogLineInfo } from '@/domains/sales/wizard/api/useCatalogLineInfo';
import { anchorLineOf } from '@/domains/sales/wizard/buildSale';
import { sureKeepFinances } from '@/domains/sales/wizard/cardBenefits';
import {
    creditPromotionRows,
    creditPromotionsForItem,
    type CreditPromotionRow,
} from '@/domains/sales/wizard/creditPromotions';
import { effectChips } from '@/domains/sales/wizard/promotionDetail';
import type { ReservedLine, WizardSession } from '@/domains/sales/wizard/types';
import { cn } from '@/lib/cn';
import { catalogDisplayName } from '@/shared/lib/format/itemDisplayName';
import { formatMXN } from '@/shared/lib/format/money';
import { cachedEvaluation, useStoreSalePromotions } from '../api/useWizardPromotions';
import {
    byName,
    heldCodeIsSureKeepOnly,
    heldCodePromotionOf,
    releasedPromotionsOf,
    salePromotionsOf,
    splitForTrack,
    trackTotalCents,
    uniqueByPromotion,
    type PaymentPromotion,
    type PaymentTrack,
    type PromotionFacts,
} from '../lib/paymentOptionPromotions';
import { releasedFor } from '../lib/sureKeepRelease';
import { useSessionPromotionState } from '../store/promotionSelectionStore';
import { isSureKeepOnly } from '../lib/promotionAppliesTo';
import { rememberedPromotionName, rememberedSureKeepOnly } from '../lib/promotionNames';
import { afterSaleText } from './AfterSaleBenefits';

/** Chips visibles antes de juntar el resto en «+N». */
const MAX_CHIPS = 3;

type ChipTone = 'keep' | 'lose' | 'gain';

const CHIP_TONE: Record<ChipTone, string> = {
    keep: 'border-success/40 bg-success-soft text-success',
    lose: 'border-destructive/30 bg-destructive/10 text-destructive line-through',
    gain: 'border-promo/40 bg-promo-soft text-promo',
};

interface Chip {
    key: string;
    label: string;
    tone: ChipTone;
}

const fmt = (cents: number): string => formatMXN(cents / 100);

/** Nombre, alcance y lo que entrega de cada promo, con lo que ya está en caché. */
function usePromotionFacts(sessionId: string): (promotionId: string) => PromotionFacts {
    const queryClient = useQueryClient();
    const store = useStoreSalePromotions(sessionId).data?.items ?? [];
    const evaluation = cachedEvaluation(sessionId, queryClient);
    return (promotionId) => {
        const listed = store.find((p) => p.promotionId === promotionId);
        const evaluated = evaluation?.promotions.find((p) => p.promotionId === promotionId);
        const known = listed ?? evaluated;
        const afterSale = (evaluated?.afterSale ?? [])
            .map(afterSaleText)
            .filter(Boolean)
            .join(' · ');
        return {
            name:
                listed?.name ??
                evaluated?.name ??
                rememberedPromotionName(sessionId, promotionId) ??
                'Promo de venta',
            sureKeepOnly: known
                ? isSureKeepOnly(known)
                : rememberedSureKeepOnly(sessionId, promotionId),
            text: afterSale !== '' ? afterSale : (listed?.customerText ?? ''),
            ...(known?.paymentOptions !== undefined && { paymentOptions: known.paymentOptions }),
            categories: listed?.scope.productCategories ?? null,
        };
    };
}

/** Lo que baja el código según la evaluación en caché, si aplica. */
function evaluatedCodeCents(
    queryClient: QueryClient,
    sessionId: string,
    code: string,
): number | undefined {
    const evaluated = cachedEvaluation(sessionId, queryClient)?.discountCode;
    return evaluated?.applicable && evaluated.codeText === code
        ? evaluated.totalDiscountCents
        : undefined;
}

/** Promos de crédito SureKeep del teléfono para el nivel del cliente (todas si aún no hay nivel). */
function useCreditRows(sessionId: string, session: WizardSession): CreditPromotionRow[] {
    const catalog = useCatalog(sessionId, { cached: true }).data;
    const anchor = anchorLineOf(session);
    const item = anchor && catalog?.items.find((i) => i.sku === anchor.sku);
    if (!anchor || !item || !sureKeepFinances(anchor)) return [];
    const level = session.loan?.level;
    if (level === 'NOT_ELIGIBLE') return [];
    return creditPromotionsForItem(
        creditPromotionRows(catalog?.scopeWidePromotions ?? [], [item], level),
        item,
    );
}

function ChipRow({ chips }: { chips: readonly Chip[] }) {
    if (chips.length === 0) return null;
    // Lo que se pierde nunca se esconde en «+N».
    const visible = Math.max(MAX_CHIPS, chips.filter((c) => c.tone === 'lose').length);
    const shown = chips.slice(0, visible);
    const rest = chips.slice(visible);
    return (
        <div data-testid="payment-promo-chips" className="mt-2 flex min-w-0 flex-wrap gap-1.5">
            {shown.map((chip) => (
                <span
                    key={chip.key}
                    title={chip.label}
                    data-tone={chip.tone}
                    className={cn(
                        'inline-block min-w-0 max-w-[11rem] truncate rounded-full border px-2 py-0.5 text-[11px] font-semibold',
                        CHIP_TONE[chip.tone],
                    )}
                >
                    {chip.label}
                </span>
            ))}
            {rest.length > 0 && (
                <span
                    title={rest.map((c) => c.label).join(', ')}
                    className="flex-none rounded-full border border-border px-2 py-0.5 text-[11px] font-semibold text-muted-foreground"
                >
                    +{rest.length}
                </span>
            )}
        </div>
    );
}

interface DetailRow {
    key: string;
    name: string;
    product?: string;
    /** Monto que baja; sin él, `text` dice lo que entrega. */
    amount?: string;
    text?: string;
}

/** Renglones de dos líneas del mismo alto: nombre y monto arriba, producto y lo que entrega abajo. */
function DetailList({
    title,
    tone,
    rows,
}: {
    title: string;
    tone: ChipTone;
    rows: readonly DetailRow[];
}) {
    if (rows.length === 0) return null;
    return (
        <div className="min-w-0">
            <div
                className={cn(
                    'mb-1 text-[10px] font-bold uppercase tracking-[0.06em]',
                    tone === 'keep' && 'text-success',
                    tone === 'lose' && 'text-destructive',
                    tone === 'gain' && 'text-promo',
                )}
            >
                {title}
            </div>
            <ul className="flex flex-col gap-1">
                {rows.map((row) => {
                    const detail = [row.product, row.text].filter(Boolean).join(' · ');
                    return (
                        <li key={row.key} className="min-w-0 text-[12px] leading-tight">
                            <div className="flex min-w-0 items-baseline justify-between gap-2">
                                <span
                                    className={cn(
                                        'min-w-0 truncate',
                                        tone === 'lose' && 'line-through',
                                    )}
                                    title={row.name}
                                >
                                    {row.name}
                                </span>
                                {row.amount && (
                                    <span
                                        className={cn(
                                            'flex-none font-semibold tabular-nums',
                                            tone === 'lose' && 'text-muted-foreground line-through',
                                            tone === 'gain' && 'text-promo',
                                        )}
                                    >
                                        {row.amount}
                                    </span>
                                )}
                            </div>
                            <div
                                className="min-w-0 truncate text-[11px] text-muted-foreground"
                                title={detail}
                            >
                                {row.product && <span>{row.product}</span>}
                                {row.product && row.text && ' · '}
                                {row.text && <span>{row.text}</span>}
                                {detail === '' && '\u00a0'}
                            </div>
                        </li>
                    );
                })}
            </ul>
        </div>
    );
}

const saleRow = (p: PaymentPromotion): DetailRow => ({
    key: `${p.promotionId}|${p.lineId}`,
    name: p.name,
    product: p.productName,
    ...(p.amountCents > 0 ? { amount: `−${fmt(p.amountCents)}` } : { text: p.text }),
});

export interface PaymentOptionPromotionsProps {
    sessionId: string;
    session: WizardSession;
    track: PaymentTrack;
    /** Total de la venta con lo que tiene apartado ahora. */
    saleTotalCents: number;
}

export function PaymentOptionPromotions({
    sessionId,
    session,
    track,
    saleTotalCents,
}: PaymentOptionPromotionsProps) {
    const [open, setOpen] = useState(false);
    const queryClient = useQueryClient();
    const facts = usePromotionFacts(sessionId);
    const { descriptions } = useCatalogLineInfo(sessionId);
    const { sureKeepReleased, anyFinancierCodes = [] } = useSessionPromotionState(sessionId);
    const released = releasedFor(sureKeepReleased, anchorLineOf(session)?.lineId);
    const productName = (line: ReservedLine): string =>
        catalogDisplayName({ sku: line.sku, ...line.product }, descriptions);
    const code = heldCodePromotionOf(session, {
        sureKeepOnly: (c) => heldCodeIsSureKeepOnly(session, c, anyFinancierCodes),
        productName,
        evaluatedCents: (c) => evaluatedCodeCents(queryClient, sessionId, c),
    });
    const held = [...salePromotionsOf(session, { facts, productName }), ...(code ? [code] : [])];
    const promos = [
        ...held,
        ...releasedPromotionsOf(session, released, held, { facts, productName }),
    ].sort(byName);
    const credit = useCreditRows(sessionId, session);
    const { kept, lost } = splitForTrack(promos, track);
    const gained = track === 'SUREKEEP' ? credit : [];
    const totalCents = trackTotalCents(
        saleTotalCents,
        promos,
        track,
        (lineId) => session.reservedLines?.find((l) => l.lineId === lineId)?.discountCents,
    );

    const chips: Chip[] = [
        ...uniqueByPromotion(lost).map((p) => ({
            key: `l-${p.promotionId}`,
            label: p.name,
            tone: 'lose' as const,
        })),
        ...uniqueByPromotion(kept).map((p) => ({
            key: `k-${p.promotionId}`,
            label: p.name,
            tone: 'keep' as const,
        })),
        ...gained.map((row) => ({
            key: `g-${row.promotionId}`,
            label: row.name,
            tone: 'gain' as const,
        })),
    ];
    const hasDetail = chips.length > 0;

    return (
        <div className="min-w-0">
            {track !== 'EXTERNAL' && (
                <div className="mt-2 flex flex-wrap items-baseline gap-2 text-[12px]">
                    <span className="text-[10px] font-semibold uppercase tracking-[0.04em] text-muted-foreground">
                        {track === 'SUREKEEP' ? 'Total' : 'Monto'}
                    </span>
                    <span className="text-[14px] font-semibold tabular-nums">
                        {fmt(totalCents)}
                    </span>
                </div>
            )}
            <ChipRow chips={chips} />
            {hasDetail && open && (
                <div className="mt-2 flex min-w-0 flex-col gap-2">
                    {track === 'SUREKEEP' ? (
                        <>
                            <DetailList
                                title="Con cualquier forma de pago"
                                tone="keep"
                                rows={kept.filter((p) => !p.sureKeepOnly).map(saleRow)}
                            />
                            <DetailList
                                title="Solo con crédito SureKeep"
                                tone="keep"
                                rows={kept.filter((p) => p.sureKeepOnly).map(saleRow)}
                            />
                        </>
                    ) : (
                        <>
                            <DetailList title="Conserva" tone="keep" rows={kept.map(saleRow)} />
                            <DetailList
                                title="Pierde · solo aplican con crédito SureKeep"
                                tone="lose"
                                rows={lost.map(saleRow)}
                            />
                        </>
                    )}
                    <DetailList
                        title="Gana con crédito SureKeep"
                        tone="gain"
                        rows={gained.map((row) => ({
                            key: row.promotionId,
                            name: row.name,
                            text: effectChips(row.effect)
                                .map((c) => c.short)
                                .join(' · '),
                        }))}
                    />
                </div>
            )}
            {hasDetail && (
                <button
                    type="button"
                    aria-expanded={open}
                    onClick={() => {
                        setOpen((v) => !v);
                    }}
                    className="mt-1.5 text-[12px] text-muted-foreground underline underline-offset-2"
                >
                    {open ? 'Ocultar detalle' : 'Ver detalle'}
                </button>
            )}
        </div>
    );
}
